import { visuallyHidden } from '@mui/utils';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, TableSortLabel } from '@mui/material';

import { useTranslation } from 'react-i18next';

import { isIterableArray, toFormatPrice, toStringPrice, } from '../../utils/utils';
import { OrderType } from '../tableHelpers/tableConfig';
import TableEmptyRows from '../tableHelpers/TableEmptyRows';
import { BalancesHeadCell, BalancesRowType } from './BalancesPage.types';

type PropsType = {
    isLoadingTable: boolean,
    rows: BalancesRowType[],
    visibleRows: BalancesRowType[],
    orderBy: keyof BalancesRowType,
    order: OrderType,
    service_id: any,
    handleRequestSort: any,
}
const BalancesTableDumb = (props: PropsType): JSX.Element => {

    const { t } = useTranslation()
    const {
        isLoadingTable,
        rows,
        visibleRows,
        orderBy,
        order,
        service_id,
        handleRequestSort
    } = props

    // ______SERVICE COLORS

    const hoverRowService = service_id === 1 ? 'hover:!bg-orange-50' : service_id === 2 ? 'hover:!bg-blue-100' : 'hover:!bg-gray-200'
    const sortIconService = service_id === 1 ? 'svg-sort-orange' : service_id === 2 ? 'svg-sort-blue' : ''





    function TableHeadComponent() {
        const createSortHandler = (property: keyof BalancesRowType) => (event: React.MouseEvent<unknown>) => {
            handleRequestSort(event, property);
        };
        return (
            <TableHead className='table-head-border border-none font-sans'>
                <TableRow>
                    {headCells.map((headCell) => (
                        <TableCell
                            key={headCell.id}
                            align={headCell.align}
                            padding={'normal'}
                            sx={{ paddingBottom: '0px !important', paddingTop: '0px !important', border: 'none' }}
                            className={`!font-sans ${headCell.className}`}
                        >
                            {
                                headCell.sorting ?
                                    <TableSortLabel
                                        active={orderBy === headCell.id}
                                        direction={orderBy === headCell.id ? order : 'asc'}
                                        onClick={createSortHandler(headCell.id)}
                                        className={`${sortIconService} text-base !text-gray text-nowrap`}
                                    >
                                        {t(headCell.label)}
                                        {orderBy === headCell.id ? (
                                            <Box component="span" sx={visuallyHidden} >
                                                <>
                                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                </>
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                    :
                                    <span className='text-base font-normal !text-gray-600 whitespace-nowrap'>
                                        {t(headCell.label)}
                                    </span>
                            }
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
        );
    }
    function TableRowsRender({ cells, row }: { cells: BalancesHeadCell[], row: BalancesRowType }) {
        return (
            <>
                {
                    cells.map((cell, index) => {
                        const key = cell.dataIndex
                        return (
                            <TableCell
                                key={cell.dataIndex}
                                align={cell.align}
                                sx={{
                                    border: 'none', lineHeight: '120%',
                                    borderTopRightRadius: index === cells.length - 1 ? 16 : 0,
                                    borderBottomRightRadius: index === cells.length - 1 ? 16 : 0,
                                    borderTopLeftRadius: index === 0 ? 16 : 0,
                                    borderBottomLeftRadius: index === 0 ? 16 : 0,
                                }}
                                padding={'normal'}
                                className='!font-sans !text-gray-700 !text-[16px]'
                            >
                                {
                                    cell.render ?
                                        cell.render(row[key], row)
                                        :
                                        row[key]
                                }
                            </TableCell>
                        )
                    })
                }
            </>
        )
    }

    const headCells: BalancesHeadCell[] = [
        {
            dataIndex: 'period', // какое поле в таблице
            id: 'period_sorter', // какое поле в сортировке
            align: 'left',
            sorting: true,
            label: 'acts_page.period',
            className: 'min-w-[80px]',
            render: (item: any, record?: any) => (
                <span className='text-gray-700'>
                    {item}
                </span>
            )
        },
        {
            dataIndex: 'balance_id',
            id: 'balance_id',
            sorting: true,
            align: 'left',
            label: 'balances_page.balance_id',
            className: 'min-w-[170px]',
            render: (item: any, record?: any) => (
                <span className='text-gray-700'>
                    {item}
                </span>
            )
        },
        {
            dataIndex: 'debet_start',
            id: 'debet_start',
            sorting: true,
            align: 'left',
            label: 'balances_page.debet_start',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'debet_nds_start',
            id: 'debet_nds_start',
            sorting: true,
            align: 'left',
            label: 'balances_page.debet_nds_start',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'credit_start',
            id: 'credit_start',
            sorting: true,
            align: 'left',
            label: 'balances_page.credit_start',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'credit_nds_start',
            id: 'credit_nds_start',
            sorting: true,
            align: 'left',
            label: 'balances_page.credit_nds_start',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'count_act',
            id: 'count_act',
            sorting: true,
            align: 'left',
            label: 'balances_page.count_act',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toStringPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'count_delta',
            id: 'count_delta',
            sorting: true,
            align: 'left',
            label: 'balances_page.count_delta',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toStringPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'amount_act',
            id: 'amount_act',
            sorting: true,
            align: 'left',
            label: 'balances_page.amount_act',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'amount_act_nds',
            id: 'amount_act_nds',
            sorting: true,
            align: 'left',
            label: 'balances_page.amount_act_nds',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'amount_delta',
            id: 'amount_delta',
            sorting: true,
            align: 'left',
            label: 'balances_page.amount_delta',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'amount_delta_nds',
            id: 'amount_delta_nds',
            sorting: true,
            align: 'left',
            label: 'balances_page.amount_delta_nds',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'payment',
            id: 'payment',
            sorting: true,
            align: 'left',
            label: 'balances_page.payment',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'payment_nds',
            id: 'payment_nds',
            sorting: true,
            align: 'left',
            label: 'balances_page.payment_nds',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'transfer',
            id: 'transfer',
            sorting: true,
            align: 'left',
            label: 'balances_page.transfer',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'transfer_nds',
            id: 'transfer_nds',
            sorting: true,
            align: 'left',
            label: 'balances_page.transfer_nds',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'debet_end',
            id: 'debet_end',
            sorting: true,
            align: 'left',
            label: 'balances_page.debet_end',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'debet_nds_end',
            id: 'debet_nds_end',
            sorting: true,
            align: 'left',
            label: 'balances_page.debet_nds_end',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'credit_end',
            id: 'credit_end',
            sorting: true,
            align: 'left',
            label: 'balances_page.credit_end',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
        {
            dataIndex: 'credit_nds_end',
            id: 'credit_nds_end',
            sorting: true,
            align: 'left',
            label: 'balances_page.credit_nds_end',
            className: 'min-w-[130px]',
            render: (item: any, record: any) => (
                <div className={`text-gray-700`}>
                    {toFormatPrice(item)}
                </div>
            )
        },
    ];


    return (
        <>
            {/* className='hidden tab:block' */}
            <TableContainer >
                <Table
                    sx={{
                        minWidth: 750,
                        borderCollapse: 'separate',
                        borderSpacing: '0 16px',
                    }}
                    size={'medium'}
                >
                    <TableHeadComponent />
                    <TableBody>
                        {
                            isIterableArray(visibleRows) ?
                                visibleRows.map((row, index) => {
                                    return (
                                        <TableRow
                                            key={row.key}
                                            hover
                                            role="checkbox"
                                            tabIndex={-1}
                                            className={`  
                                                table-row-border
                                                service_id_${service_id}
                                                ${hoverRowService}
                                            `}
                                        >
                                            <TableRowsRender cells={headCells} row={row} />
                                        </TableRow>
                                    );
                                })
                                :
                                <TableEmptyRows isLoadingTable={isLoadingTable} />
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default BalancesTableDumb
